/**
 * DynamoDB key builders for CnapseGatewayConfigPush (§9.5).
 */

export const GATEWAY_CONFIG_PUSH_GATEWAY_TYPES = ['efem', 'location'] as const;
export type GatewayConfigPushGatewayType = (typeof GATEWAY_CONFIG_PUSH_GATEWAY_TYPES)[number];

export const GATEWAY_CONFIG_PUSH_STATUSES = [
  's3_uploaded',
  'mqtt_sent',
  's3_uploaded_mqtt_failed',
  'applied',
  'apply_failed',
  'apply_timeout',
  'superseded',
] as const;
export type GatewayConfigPushStatus = (typeof GATEWAY_CONFIG_PUSH_STATUSES)[number];

export const PENDING_SUPERSEDE_STATUSES: GatewayConfigPushStatus[] = [
  'mqtt_sent',
  's3_uploaded',
  'apply_timeout',
];

/** Push rows that can transition to `applied` when the edge reports a matching config version. */
export const AWAITING_EDGE_APPLY_ACK_STATUSES: GatewayConfigPushStatus[] = [
  'mqtt_sent',
  'apply_timeout',
];

export function gatewayConfigPushPk(
  gatewayType: GatewayConfigPushGatewayType,
  gatewaySerialNumber: string,
): string {
  return `GATEWAY#${gatewayType}#${gatewaySerialNumber}`;
}

export function gatewayConfigPushSk(configVersion: string): string {
  return `PUSH#${configVersion}`;
}

export function gatewayConfigPushGsi1Keys(
  tenantId: string,
  scopeId: string,
  pushedAt: string,
  gatewayType: GatewayConfigPushGatewayType,
  gatewaySerialNumber: string,
): { gsi1pk: string; gsi1sk: string } {
  return {
    gsi1pk: `TENANT#${tenantId}#SITE#${scopeId}`,
    gsi1sk: `PUSH#${pushedAt}#${gatewayType}#${gatewaySerialNumber}`,
  };
}

export function gatewayConfigPushGsi2Keys(
  mqttSentAt: string,
  tenantId: string,
  gatewaySerialNumber: string,
  configVersion: string,
): { gsi2pk: string; gsi2sk: string } {
  return {
    gsi2pk: 'STATUS#mqtt_sent',
    gsi2sk: `${mqttSentAt}#${tenantId}#${gatewaySerialNumber}#${configVersion}`,
  };
}

/** Upper bound for GSI2 time sweep (gsi2sk prefix is mqttSentAt ISO). */
export function gatewayConfigPushGsi2CutoffSk(cutoffIso: string): string {
  return `${cutoffIso}#`;
}

export function parseGatewayConfigPushPk(pk: string): {
  gatewayType: GatewayConfigPushGatewayType;
  gatewaySerialNumber: string;
} {
  const match = /^GATEWAY#(efem|location)#(.+)$/.exec(pk);
  if (!match?.[1] || !match[2]) {
    throw new Error(`Invalid CnapseGatewayConfigPush pk: ${pk}`);
  }
  return {
    gatewayType: match[1] as GatewayConfigPushGatewayType,
    gatewaySerialNumber: match[2],
  };
}

export function parseGatewayConfigPushSk(sk: string): string {
  if (!sk.startsWith('PUSH#')) {
    throw new Error(`Invalid CnapseGatewayConfigPush sk: ${sk}`);
  }
  return sk.slice('PUSH#'.length);
}
